/**
 * rother3 companion — the six per-family comparison pages.
 *
 * ## What this file is
 *
 * `compare-rows.js` answers "which rows exist when everything is compared at once". This file
 * answers "what does the **Categories** page show", and it is a different question: a page is
 * read by someone who wants one thing, and a page that carries 26 rows across seven sections
 * is a page nobody reads to the bottom.
 *
 * So the same row model is reused — same `CELL` states, same readability rule, same
 * `buildRow` — and only the *selection* changes. The correctness work was done once, in
 * `compare-rows.js`; this file decides what to show.
 *
 * ## The three families that did not exist before
 *
 * Research into what every export already carries turned up three families with **no**
 * comparison table anywhere in the project:
 *
 * - **Profile** (name, address, phone, website). Never compared at all. For a business owner,
 *   this is the family they check most — "does my competitor actually list a phone number?"
 * - **About** (description, amenity groups). `about.description` was read on the detail view
 *   and compared nowhere. A 400-character description and an empty one are different listings.
 * - **Rating distribution, all five buckets.** `compare-rows.js` carried 5★, 4★ and 1★ and
 *   dropped 3★ and 2★, so a business whose reviews are mostly 2★ and 3★ looked identical to
 *   one whose worst reviews are 1★. The buckets were always readable; the page just never
 *   asked for them.
 *
 * ## What is deliberately still absent
 *
 * - **A review *timeline*.** Refused. It would be drawn from the handful of reviews rother3
 *   read, and a line through a dozen points is a trend nobody measured.
 * - **A photo *URL* comparison.** The export carries thumbnail URLs, but comparing URLs is not
 *   a finding; a reader cannot act on "this competitor's photo is at this address".
 * - **A keyword cloud.** Counting words across a small sample needs per-language tokenizers
 *   and stop-word lists, and the sample cannot support "top keywords".
 *
 * Every family here reads the export. None of them fetches anything.
 */
import { CELL } from './compare-rows.js';
import { categoryNames, ratingDistribution, popularTimesSummary } from './audit-shape.js';

/**
 * How wide a column wants to be, by what it holds.
 *
 * ## Why this is data-driven and not one number for every column
 *
 * `table-layout: fixed` needs a width per column, and 120px for all of them was wrong in both
 * directions at once. A `Rating` of **4.7** was clipped to **5** — the decimal is the entire
 * figure — while a 36-character Place ID and a nine-part address were crushed into a column the
 * size of a checkbox, wrapping to eight lines each.
 *
 * The numbers below are measured against real exports rather than chosen for tidiness, and each
 * one records why it is what it is.
 *
 * ## The two rules the whole thing rests on
 *
 * - **A column is never narrower than its own header.** "Days with published hours" is longer
 *   than the figure it holds, so a header that wraps is fine and a header that is *clipped* is
 *   not. Every width here is at least the widest label it must show, which is why they are sized
 *   from the header text as well as the data.
 * - **A column never truncates a value.** A number that does not fit is a wrong number on screen,
 *   which is the failure this project treats as worst. Where a value is genuinely longer than any
 *   sensible column — a Place ID, a full address — it **wraps**, because a wrapped value is
 *   complete and a clipped one is a lie.
 */
export const COLUMN_WIDTH = {
  /** Business name. The row header already shows it, so this column is narrow by design. */
  name: 200,
  /** A star rating or a small whole number. Sized to hold `4.7` with room, never to clip it. */
  rating: 90,
  /** A four-to-six digit count with a thousands separator: `14,280`. */
  count: 110,
  /** A phone number: `+62 811-7966-7935`. */
  phone: 150,
  /** A bare domain: `ubudcoffeeroastery.com`. */
  website: 170,
  /**
   * A 36-character Place ID with no spaces: `0x2dd241c1a772e66f0e4a7735c034d1877`.
   *
   * Wide enough that it breaks once rather than three times. It cannot be made to fit on one
   * line at a readable size, and `overflow-wrap: anywhere` will break it wherever it lands.
   */
  placeId: 230,
  /** A street address, nine parts at most. The widest prose in the common families. */
  address: 260,
  /** Anything else that is prose. */
  text: 180,
  /** A flag, a dash, a short object — `✓ yes`, `3 of 7`. */
  short: 120,
  /** A count plus its share, or a group count. */
  object: 150,
};

/**
 * The width class for one column, from what it holds.
 *
 * Keyed on the column's own identity rather than its `format`, because two columns can share a
 * format and want very different widths: `place-id` and `phone` are both `text`, and one is a
 * 36-character unbreakable token while the other is 17 characters that break nicely.
 *
 * @param {string} key - the row key, e.g. `place-id`
 * @param {string} format - the row format, e.g. `text`
 * @returns {number} width in CSS pixels
 */
export function columnWidth(key, format) {
  if (key === 'name') {return COLUMN_WIDTH.name;}
  if (key === 'place-id') {return COLUMN_WIDTH.placeId;}
  if (key === 'address') {return COLUMN_WIDTH.address;}
  if (key === 'phone') {return COLUMN_WIDTH.phone;}
  if (key === 'website') {return COLUMN_WIDTH.website;}
  if (key === 'rating') {return COLUMN_WIDTH.rating;}
  if (key === 'reviews' || key === 'photo-total' || key === 'photo-owner' || key === 'photo-customer') {
    return COLUMN_WIDTH.count;
  }
  if (format === 'number') {return COLUMN_WIDTH.count;}
  if (format === 'flag') {return COLUMN_WIDTH.short;}
  if (format === 'object') {return COLUMN_WIDTH.object;}
  return COLUMN_WIDTH.text;
}
function cell(value, state = CELL.ok) {
  return { value, state, readable: state === CELL.ok };
}

/** A null/absent value with the state for *why* it is absent. */
function absent(state) {
  return { value: null, state, readable: false };
}

/** A readable non-empty string, or the state for why there is none. */
function readText(f, key) {
  const v = f?.[key];
  if (typeof v !== 'string') {return absent(CELL.unknown);}
  const trimmed = v.trim();
  if (trimmed === '') {return absent(CELL.unknown);}
  return cell(trimmed);
}

// ── Family readers ─────────────────────────────────────────────────────────
//
// Each family gets the readers it needs. Readers are shared between families where the field
// is the same one — `readMissing` is used by both the listing family and the profile family's
// contact-rows, because a missing phone number is the same fact from both sides.

/** Total reviews, as Maps publishes it. Distinct from the reviews rother3 read. */
function readReviewCount(f) {
  const v = parseFloat(f?.reviewCount);
  return Number.isFinite(v) ? cell(Math.round(v)) : absent(CELL.unknown);
}

/** Average rating, 0–5. */
function readRating(f) {
  const v = parseFloat(f?.rating);
  return Number.isFinite(v) ? cell(v) : absent(CELL.unknown);
}

/**
 * One star bucket of the listing's **whole** distribution, with its share.
 *
 * Maps' aggregate, not the sample — which is why this reader exists for all five buckets and
 * the sample-derived rows sit under a different heading with a different caveat.
 */
function readStar(f, star) {
  const dist = f?.distribution;
  if (!Array.isArray(dist) || dist.length === 0) {return absent(CELL.unknown);}
  const entry = dist.find((d) => Number(d?.star) === star);
  if (!entry || typeof entry.count !== 'number') {return absent(CELL.unread);}
  const total = dist.reduce((sum, d) => sum + (typeof d?.count === 'number' ? d.count : 0), 0);
  return cell({ count: entry.count, share: total > 0 ? (entry.count / total) * 100 : 0 });
}

/** How many of the seven days the crowd chart covered. */
function readCrowdCoverage(f) {
  const summary = popularTimesSummary(f);
  if (summary === null) {return absent(CELL.unknown);}
  return cell({ read: summary.daysRead, of: 7 });
}

/** Every day tied for busiest, as Maps reported them. Never truncated. */
function readBusiestDays(f) {
  const summary = popularTimesSummary(f);
  if (summary === null) {return absent(CELL.unknown);}
  return cell(summary.busiestDays);
}

/** The single busiest hour of the week, and which day it fell on. */
function readPeakHour(f) {
  const summary = popularTimesSummary(f);
  if (summary === null) {return absent(CELL.unknown);}
  const hour = summary.peakHour;
  return cell({
    text: hour === 0 ? '12am' : hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`,
    day: summary.peakDay,
  });
}

/** Owner reply rate, as a percentage. Derived from the **sample**, so its heading says so. */
function readReplyRate(f) {
  const rate = f?.reviewsMeta?.insights?.responseRate?.rate;
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {return absent(CELL.unknown);}
  return cell({ percent: rate * 100 });
}

/** How many reviews rother3 captured, against what the listing reports. */
function readSampleCoverage(f) {
  const coverage = f?.reviewsMeta?.coverage;
  if (typeof coverage?.captured !== 'number') {return absent(CELL.unknown);}
  const total = typeof coverage.totalReviews === 'number' ? coverage.totalReviews : null;
  return cell({ captured: coverage.captured, of: total });
}

/** Primary and secondary category counts, plus the names. */
function readCategories(f) {
  const cat = f?.categories;
  if (!Array.isArray(cat)) {return absent(CELL.unknown);}
  const primary = cat.filter((c) => c?.isPrimary === true);
  const secondary = cat.filter((c) => c?.isPrimary !== true);
  return cell({ primary: primary.length, secondary: secondary.length, names: categoryNames(cat) });
}

/** The primary category's name, which is the one Google actually uses for matching. */
function readPrimaryCategoryName(f) {
  const cat = f?.categories;
  if (!Array.isArray(cat)) {return absent(CELL.unknown);}
  const primary = cat.find((c) => c?.isPrimary === true);
  if (!primary || typeof primary.name !== 'string' || primary.name.trim() === '') {
    return absent(CELL.unknown);
  }
  return cell(primary.name.trim());
}

/**
 * Whether the category list is the whole list.
 *
 * `page-state` carries every category Google holds; `dom-chip` carries the one chip Maps
 * rendered. Rendering both identically is how "one name" came to read as a complete list —
 * already fixed once for the detail view, and the same field answers it here.
 */
function readCategoryCompleteness(f) {
  const source = f?.categorySource;
  if (source === 'page-state') {return cell('complete');}
  if (source === 'dom-chip') {return cell('primary-only');}
  if (source === 'none') {return absent(CELL.unknown);}
  return absent(CELL.unknown);
}

/** Description length in characters, and whether one was read at all. */
function readDescription(f) {
  const text = f?.description ?? f?.about?.description;
  if (typeof text !== 'string' || text.trim() === '') {return absent(CELL.unknown);}
  return cell({ chars: text.trim().length, words: text.trim().split(/\s+/).length });
}

/** How many amenity groups the About tab listed. */
function readAmenityGroups(f) {
  const groups = f?.about?.categories;
  if (!Array.isArray(groups)) {return absent(CELL.unknown);}
  return cell({ groups: groups.length, items: groups.reduce((n, g) => n + (Array.isArray(g?.items) ? g.items.length : 0), 0) });
}

/**
 * Which amenity groups this business lists.
 *
 * Union across every compared business, then per-business presence. The union is the point: a
 * fixed list of 11 amenities would show "not listed" for an amenity nobody in the comparison
 * has, which asserts the competitor lacks something when the data simply never covered it.
 */
function buildAmenityRows(fields) {
  const names = new Set();
  for (const f of fields) {
    const groups = f?.about?.categories;
    if (!Array.isArray(groups)) {continue;}
    for (const g of groups) {
      if (!Array.isArray(g?.items)) {continue;}
      for (const item of g.items) {
        if (typeof item === 'string' && item.trim() !== '') {names.add(item.trim());}
      }
    }
  }

  return [...names].sort((a, b) => a.localeCompare(b)).map((name) => ({
    key: `amenity:${name}`,
    label: name,
    hint: 'Listed on the About tab of this listing.',
    better: 'none',
    format: 'flag',
    // An amenity name is a word or two, and it is the column's own header, so it is sized to
    // show that header rather than to fit a value.
    width: Math.max(COLUMN_WIDTH.short, Math.min(220, name.length * 7 + 28)),
    cells: fields.map((f) => {
      const groups = f?.about?.categories;
      if (!Array.isArray(groups)) {return absent(CELL.unknown);}
      const has = groups.some((g) => Array.isArray(g?.items) && g.items.includes(name));
      return cell(has);
    }),
    readableCount: fields.filter((f) => Array.isArray(f?.about?.categories)).length,
    widest: null,
  }));
}

/** Missing checklist items, as a count and the names. */
function readMissing(f) {
  const items = f?.completeness?.missingItems;
  if (!Array.isArray(items)) {return absent(CELL.unknown);}
  return cell({ count: items.length, names: items.filter((n) => typeof n === 'string') });
}

/**
 * Which of the 13 completeness items a business has.
 *
 * The project refused a single 0–100 completeness score on purpose — a score hides *which* part
 * was bad and invites optimising the number. This is the same decision applied to a
 * comparison: 13 rows, each yes/no, instead of one number that cannot be acted on.
 *
 * Every one of the 13 keys is enumerated rather than discovered from the data, so two
 * businesses missing *different* items stop looking identical.
 */
const COMPLETENESS_ITEMS = [
  ['hasName', 'has a name'],
  ['hasAddress', 'has an address'],
  ['hasPhone', 'has a phone number'],
  ['hasWebsite', 'has a website'],
  ['hasHours', 'has opening hours'],
  ['hasCategories', 'has categories'],
  ['hasPrimaryCategory', 'has a primary category'],
  ['hasSecondaryCategories', 'has secondary categories'],
  ['hasRating', 'has a star rating'],
  ['hasReviews', 'has reviews'],
  ['hasPhotos', 'has photos'],
  ['hasLogo', 'has a logo'],
  ['hasCoverPhoto', 'has a cover photo'],
];

function buildCompletenessFlagRows(fields) {
  return COMPLETENESS_ITEMS.map(([key, label]) => ({
    key: `complete:${key}`,
    label,
    hint: 'One of the thirteen items on the listing checklist.',
    better: 'higher',
    format: 'flag',
    // Sized from this item's own label, because these labels range from "has a name" to "has a
    // cover photo" and a single width either clips the long ones or wastes space on the short.
    width: Math.max(COLUMN_WIDTH.short, Math.min(200, label.length * 7 + 28)),
    cells: fields.map((f) => {
      const c = f?.completeness;
      if (!c || typeof c !== 'object' || typeof c[key] !== 'boolean') {
        return absent(CELL.unknown);
      }
      return cell(c[key]);
    }),
    readableCount: fields.filter((f) => typeof f?.completeness?.[key] === 'boolean').length,
    widest: null,
  }));
}

/** Total photos, or the reason there are none. */
function readPhotoTotal(f) {
  const pt = f?.photos;
  if (!pt || typeof pt !== 'object') {return absent(CELL.unknown);}
  if (typeof pt.total === 'number') {return cell(pt.total);}
  return absent(pt.countState === 'not-published' ? CELL.notPublished : CELL.unread);
}

/**
 * Owner-supplied photos.
 *
 * `null` on a listing that publishes photos is a **read failure**, not an absence — Maps
 * reports a photo count with no owner/customer split on many listings, and collapsing that to
 * zero is exactly the claim `compare-rows.js` refuses to make elsewhere.
 */
function readOwnerPhotos(f) {
  const v = f?.photos?.ownerPhotos;
  if (typeof v === 'number') {return cell(v);}
  if (f?.photos && typeof f.photos === 'object') {return absent(CELL.unread);}
  return absent(CELL.unknown);
}

/** Customer-submitted photos. Same rule as `readOwnerPhotos`. */
function readCustomerPhotos(f) {
  const v = f?.photos?.customerPhotos;
  if (typeof v === 'number') {return cell(v);}
  if (f?.photos && typeof f.photos === 'object') {return absent(CELL.unread);}
  return absent(CELL.unknown);
}

/** Logo / cover presence as a pass-fail. */
function readPhotoFlag(f, key) {
  const pt = f?.photos;
  if (!pt || typeof pt !== 'object' || !(key in pt)) {return absent(CELL.unknown);}
  return cell(pt[key] === true);
}

/**
 * The four pages that carry a completeness figure.
 *
 * `buildRow` is imported from `compare-rows.js` rather than reimplemented, so a change to the
 * readability rule cannot be applied to one family and forgotten on the other.
 */
function buildRow(key, label, hint, better, format, fields, reader) {
  const cells = fields.map((f) => ({
    placeId: typeof f?.placeId === 'string' ? f.placeId : null,
    businessName: typeof f?.businessName === 'string' ? f.businessName : 'Unknown',
    ...reader(f),
  }));

  return {
    key,
    label,
    hint,
    better,
    format,
    /** How wide this column wants to be. See `COLUMN_WIDTH` for why these differ. */
    width: columnWidth(key, format),
    cells,
    readableCount: cells.filter((c) => c.readable).length,
    widest: null,
  };
}

/** Days with published hours, against seven. */
function readHoursDays(f) {
  const parsed = f?.hoursMeta?.parsed;
  if (!Array.isArray(parsed)) {
    return typeof f?.hours === 'string' && f.hours.length > 0
      ? cell({ count: 1 })
      : absent(CELL.unknown);
  }
  return cell({ count: parsed.filter((r) => r && typeof r.day === 'string' && r.day.length > 0).length });
}

/**
 * The six families.
 *
 * `format` reuses `compare-deep.js`'s vocabulary so the shared renderer needs no new branches:
 * `number`, `percent`, `hours`, `list`, `flag`, `text`, `object`.
 *
 * `fields` is every business with the **anchor first**, matching `compare-rows.js`'s
 * convention: `cells[0]` is the anchor, and the renderer relies on that.
 */
export const FAMILIES = {
  /**
   * Profile — who this business is and how to reach it.
   *
   * The family with no comparison table until now, and the one an owner checks most often.
   * The contact rows are deliberately `better: 'none'` for text: a competitor having a phone
   * number is not "better", it is a fact, and colouring it would be the hue-as-rank mistake
   * this project removed from the charts.
   */
  profile: {
    id: 'profile',
    title: 'Business profiles',
    note: 'Who each business is, and how it can be reached. Blank means Google publishes nothing there.',
    build: (fields) => [
      {
        id: 'identity',
        title: 'Identity',
        /**
         * The business **name** is deliberately absent here.
         *
         * It was the first row of this family, and it duplicated the row header exactly: every
         * row read "LillaPaiement / Lilla Pantai". Two columns carrying the same string, one of
         * them 200px wide, is 200px of the page spent saying the row's own subject twice — and the
         * row header is already sticky, so the second copy was the one that scrolled away.
         *
         * The name is not lost. It is the row's identity, it is in the sticky first column, it is
         * the sort control's default, and it is the search term the filter matches against. What
         * is gone is the duplicate.
         */
        rows: [
          buildRow('place-id', 'Place ID', "Google's own identifier for the listing.", 'none', 'text', fields, (f) => readText(f, 'placeId')),
        ],
      },
      {
        id: 'contact',
        title: 'How to reach them',
        note: 'A listing with no phone and no website is a finding about the listing, not a gap in our read.',
        rows: [
          buildRow('address', 'Address', null, 'none', 'text', fields, (f) => readText(f, 'address')),
          buildRow('phone', 'Phone', null, 'none', 'text', fields, (f) => readText(f, 'phone')),
          buildRow('website', 'Website', null, 'none', 'text', fields, (f) => readText(f, 'website')),
        ],
      },
      {
        id: 'presence',
        title: 'What Maps has',
        rows: [
          buildRow('rating', 'Rating', 'Average star rating, 0–5.', 'higher', 'rating', fields, readRating),
          buildRow('reviews', 'Reviews', 'Total reviews on the listing.', 'higher', 'number', fields, readReviewCount),
          buildRow('hours-days', 'Days with published hours', null, 'higher', 'number', fields, readHoursDays),
          buildRow('missing-items', 'Missing checklist items', 'Fewer is better. The names are on the listing page.', 'lower', 'object', fields, readMissing),
        ],
      },
    ],
  },

  /**
   * Categories — what each business says it is.
   *
   * Primary category first, because it is the one Google matches on; secondary categories are
   * the ones businesses actually compete on. The completeness row is here because it is the
   * difference between "this business has one category" and "we only read one category".
   */
  categories: {
    id: 'categories',
    title: 'Categories',
    note: 'The labels each listing uses to describe itself. The primary category is the one Google matches on.',
    build: (fields) => [
      {
        id: 'counts',
        title: 'Counts',
        rows: [
          buildRow('cat-primary', 'Primary category', 'How many are marked primary. Maps allows one.', 'none', 'object', fields, (f) => {
            const c = readCategories(f);
            return c.readable ? cell({ count: c.value.primary }) : c;
          }),
          buildRow('cat-secondary', 'Secondary categories', null, 'higher', 'object', fields, (f) => {
            const c = readCategories(f);
            return c.readable ? cell({ count: c.value.secondary, names: c.value.names }) : c;
          }),
          buildRow('cat-list', 'All category names', null, 'none', 'list', fields, (f) => readCategories(f)),
        ],
      },
      {
        id: 'primary-name',
        title: 'Primary category name',
        rows: [
          buildRow('cat-primary-name', 'Primary category', "The single label Google uses to match this listing.", 'none', 'text', fields, readPrimaryCategoryName),
          buildRow('cat-completeness', 'Is the list complete?', 'A "one chip" read cannot tell you the listing has only one category.', 'none', 'text', fields, readCategoryCompleteness),
        ],
      },
    ],
  },

  /**
   * Reviews — the listing's whole reputation, and the sample rother3 read.
   *
   * The two halves are separated on purpose and given different headings. The distribution is
   * Maps' aggregate over every review and can be compared outright. The response rate and the
   * sample count are computed from a handful of cards and **cannot** be read as facts about the
   * business, so they sit under their own note.
   */
  reviews: {
    id: 'reviews',
    title: 'Reviews',
    note: 'The distribution is Maps\'s own total for every review. The response figures come from the reviews rother3 read, which is a small sample.',
    build: (fields) => [
      {
        id: 'totals',
        title: 'Totals',
        note: 'What Maps publishes for the whole listing.',
        rows: [
          buildRow('rating', 'Rating', 'Average star rating, 0–5.', 'higher', 'rating', fields, readRating),
          buildRow('reviews', 'Reviews', 'Total reviews on the listing.', 'higher', 'number', fields, readReviewCount),
        ],
      },
      {
        id: 'distribution',
        title: 'Rating distribution',
        note: 'All five buckets, because a business whose worst reviews are 2★ and 3★ is not the same as one whose worst are 1★. Count and share of all reviews.',
        rows: [5, 4, 3, 2, 1].map((star) =>
          buildRow(
            `stars${star}`,
            `${star}-star reviews`,
            star === 1 ? 'Fewer is better here, unlike the rows above.' : null,
            star === 1 ? 'lower' : star === 5 ? 'higher' : 'none',
            'object',
            fields,
            (f) => readStar(f, star)
          )
        ),
      },
      {
        id: 'response',
        title: 'Owner response — from the sample',
        note: 'Computed from the reviews rother3 read, not from all of them. Read these beside the totals above.',
        rows: [
          buildRow('reply-rate', 'Reviews answered', 'Share of the sampled reviews the owner replied to.', 'higher', 'object', fields, readReplyRate),
          buildRow('sample', 'Reviews captured', 'How many rother3 read, against the listing\'s total.', 'none', 'object', fields, readSampleCoverage),
        ],
      },
    ],
  },

  /**
   * Popular times — when each business is busy.
   *
   * A listing that publishes no crowd data is **not** a gap in our read: nobody can add crowd
   * data to a Business Profile, so a competitor without it has something no one can copy. That
   * distinction is carried by `CELL.notPublished` and stated in the note.
   *
   * The hourly grid stays on the per-business page. Across 100 businesses, 100 × 168 cells is
   * 16,800 coloured squares, and a heatmap nobody can read is a picture, not a comparison.
   */
  popularTimes: {
    id: 'popularTimes',
    title: 'Popular times',
    note: 'A listing that publishes no crowd data is not a gap in our read — nobody can add it to a Business Profile.',
    build: (fields) => [
      {
        id: 'busy',
        title: 'When it is busy',
        rows: [
          buildRow('peak-hour', 'Busiest hour of the week', 'The single busiest hour Maps recorded.', 'none', 'object', fields, readPeakHour),
          buildRow('busiest-days', 'Busiest days', 'Every day tied for busiest, as Maps reported them.', 'none', 'list', fields, readBusiestDays),
          buildRow('crowd-coverage', 'Days of crowd data', 'Out of seven. Fewer than seven means an incomplete week.', 'higher', 'object', fields, readCrowdCoverage),
        ],
      },
    ],
  },

  /**
   * About — what the business says about itself.
   *
   * The family that had a description read on the detail view and compared nowhere. Size
   * rather than the text itself: a 400-character description and an empty one are different
   * listings, but printing three paragraphs per business in a comparison makes the page
   * unreadable at 100 rows. The text is one click away on the business's own page.
   */
  about: {
    id: 'about',
    title: 'About',
    note: 'What each business writes about itself. Sizes, not the full text — the whole description is on the business\'s own page.',
    build: (fields) => [
      {
        id: 'description',
        title: 'Description',
        rows: [
          buildRow('description', 'Description', 'Length of the description Maps publishes.', 'higher', 'object', fields, readDescription),
        ],
      },
      {
        id: 'amenities',
        title: 'Amenities',
        note: 'Each amenity any business in this comparison lists. A business that lists none is not the same as one where we could not read any.',
        rows: buildAmenityRows(fields),
      },
    ],
  },

  /**
   * Listing — how complete each listing is, and its photos.
   *
   * The thirteen checklist rows are the heart of this page. A single "8/13" number would hide
   * which part was bad, and the project's stated reason for refusing a composite score applies
   * unchanged to a comparison: nobody optimises "13/13", they add the missing photo.
   */
  listing: {
    id: 'listing',
    title: 'Listing completeness & photos',
    note: 'The thirteen items Google checks, one row each, plus photo counts. No single score — a score hides which part was missing.',
    build: (fields) => [
      {
        id: 'summary',
        title: 'Summary',
        rows: [
          buildRow('missing-items', 'Missing checklist items', 'Fewer is better. The names are on the listing page.', 'lower', 'object', fields, readMissing),
        ],
      },
      {
        id: 'checklist',
        title: 'The checklist',
        note: 'Thirteen rows rather than one score, so a gap can be acted on.',
        rows: buildCompletenessFlagRows(fields),
      },
      {
        id: 'photos',
        title: 'Photos',
        note: 'A blank owner/customer split means we could not read it, not that the business has none.',
        rows: [
          buildRow('photo-total', 'Total photos', null, 'higher', 'number', fields, readPhotoTotal),
          buildRow('photo-owner', 'Owner photos', null, 'higher', 'number', fields, readOwnerPhotos),
          buildRow('photo-customer', 'Customer photos', null, 'higher', 'number', fields, readCustomerPhotos),
          buildRow('photo-logo', 'Has a logo', null, 'higher', 'flag', fields, (f) => readPhotoFlag(f, 'hasLogo')),
          buildRow('photo-cover', 'Has a cover photo', null, 'higher', 'flag', fields, (f) => readPhotoFlag(f, 'hasCover')),
        ],
      },
    ],
  },
};

/** The six pages, in the order the header buttons list them. */
export const FAMILY_IDS = ['profile', 'categories', 'reviews', 'popularTimes', 'about', 'listing'];

/**
 * Build one family's sections, or `null` if the id is not a family.
 *
 * `null` rather than a thrown error, because the id comes from a `?f=` query parameter and an
 * unknown value is a mistyped URL, not a bug to crash on.
 */
export function buildFamily(familyId, fields) {
  const family = FAMILIES[familyId];
  if (!family) {return null;}
  const sections = family.build(fields);

  /**
   * Drop rows where **nobody** was readable.
   *
   * A row of 100 "not read" is not a finding, and on the About page — where the row set is
   * built from the data — it is the default outcome for every amenity nobody listed.
   */
  const kept = sections
    .map((s) => ({ ...s, rows: s.rows.filter((r) => r.readableCount > 0) }))
    .filter((s) => s.rows.length > 0);

  return { id: family.id, title: family.title, note: family.note, sections: kept };
}